import React, { useMemo } from 'react';

/** 'lines' draws a cross-hatch grid (the classic look); 'dots' draws a single dot per cell
 * instead — same bounded pattern, same spacing/color/opacity knobs, just a different mark. */
export type GridPatternVariant = 'lines' | 'dots';

type Props = {
    /** Distance between grid lines in canvas units — also the resulting cell's side length. */
    spacing?: number;
    color?: string;
    opacity?: number;
    strokeWidth?: number;
    /** Which mark the pattern repeats. Default 'lines'. */
    variant?: GridPatternVariant;
    /** Dot radius (canvas units), used only when variant='dots'. */
    dotRadius?: number;
    /** Horizontal bounds of the covered region (canvas units). Defaults to a wide, effectively-open range. */
    xMin?: number;
    xMax?: number;
    /** Vertical bounds of the covered region (canvas units). Defaults to a wide, effectively-open range. */
    yMin?: number;
    yMax?: number;
};

/**
 * Grid pattern background for GridCanvas.tsx (twice per pane — a minor + a major layer, for any
 * of its instances) — either rows/columns forming square cells like graph/math-notebook paper
 * (variant='lines'), or a dot at each cell's center (variant='dots'), generic across whatever
 * spacing/color/variant each instance is configured with. Distinct from the OST Canvas's own dot
 * grid (GridLayer.tsx), which isn't bounded to a region the way this one is.
 */
export default function GridLines({
    spacing = 40,
    color = '#d9d9d9',
    opacity = 0.9,
    strokeWidth = 1,
    variant = 'lines',
    dotRadius = 1.5,
    xMin = -4000,
    xMax = 4000,
    yMin = -4000,
    yMax = 4000,
}: Props) {
    const id = useMemo(() => `grid-pattern-${Math.random().toString(36).slice(2, 9)}`, []);

    return (
        <g className={`grid-canvas-lines grid-canvas-lines--${variant}`} pointerEvents='none' aria-hidden='true'>
            <defs>
                <pattern id={id} patternUnits='userSpaceOnUse' width={spacing} height={spacing}>
                    {variant === 'dots' ? (
                        <circle cx={spacing / 2} cy={spacing / 2} r={dotRadius} fill={color} opacity={opacity} />
                    ) : (
                        <path
                            d={`M ${spacing} 0 L 0 0 0 ${spacing}`}
                            fill='none'
                            stroke={color}
                            strokeWidth={strokeWidth}
                            opacity={opacity}
                        />
                    )}
                </pattern>
            </defs>
            <rect x={xMin} y={yMin} width={xMax - xMin} height={yMax - yMin} fill={`url(#${id})`} />
        </g>
    );
}
